import React, { useEffect, useRef } from 'react';
import { useStore } from '@nanostores/react';
import { Info } from 'lucide-react';
import { cn } from '@ideoz/ui';
import { dismissHint, hotEditStore } from '../../stores/hotEdit';
import { GLASS, RISE } from './surface';

const WIDTH = 248;
const GAP = 10;
/** Enough for the line, two lines of it, and the button. */
const CARD_HEIGHT = 108;

/**
 * What the founder should know about the change they just made, under the element it is about.
 *
 * This used to be a toast after Apply - a message about something already written, arriving long
 * after the moment it was about, and every time. A hint arrives with the change, says one thing,
 * and "Got it" ends it for good.
 */
export function HintCard({
  frame,
}: {
  frame: { top: number; left: number; width: number; height: number } | null;
}): React.ReactElement | null {
  const hint = useStore(hotEditStore.hint);
  const rect = useStore(hotEditStore.selectionRect);
  const cardRef = useRef<HTMLDivElement>(null);

  // A click anywhere else puts it away for now; only Got it puts it away for good.
  useEffect(() => {
    if (!hint) return undefined;
    const onPointerDown = (ev: PointerEvent) => {
      if (cardRef.current?.contains(ev.target as Node)) return;
      dismissHint(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') dismissHint(false);
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [hint]);

  if (!hint || !frame) return null;

  // Under the element it is about, or in the corner of the preview when nothing is selected.
  const below = rect ? frame.top + rect.top + rect.height + GAP : frame.top + 16;
  const fitsBelow = below + CARD_HEIGHT <= frame.top + frame.height;
  const top =
    fitsBelow || !rect
      ? below
      : Math.max(frame.top + 8, frame.top + rect.top - CARD_HEIGHT - GAP);
  const left = Math.max(
    frame.left + 8,
    Math.min(frame.left + (rect?.left ?? 16), frame.left + frame.width - WIDTH - 8),
  );

  return (
    <div
      ref={cardRef}
      className={cn(GLASS, RISE, 'absolute z-40 rounded-[14px] px-3 pb-2.5 pt-3')}
      style={{ top, left, width: WIDTH }}
      role="status"
      aria-live="polite"
      data-testid="hot-edit-hint"
      onMouseDown={(e) => e.preventDefault()}
    >
      <span
        aria-hidden
        className="absolute -top-1.5 left-[22px] h-3 w-3 rotate-45"
        style={{ background: 'var(--editor-glass)', boxShadow: 'inset 1px 1px 0 0 var(--editor-edge)' }}
      />
      <p className="flex gap-2 text-xs leading-relaxed">
        <Info size={14} className="mt-px shrink-0" style={{ color: 'var(--editor-accent)' }} aria-hidden />
        {hint.text}
      </p>
      <button
        type="button"
        className="mt-2.5 h-[30px] w-full rounded-[9px] text-xs font-semibold text-white"
        style={{ background: 'var(--editor-accent)' }}
        onClick={() => dismissHint(true)}
        data-testid="hot-edit-hint-got-it"
      >
        Got it
      </button>
    </div>
  );
}
